import { marketingWebsiteApi } from "../config/apiConfig";

export type FundingCampaignStatus = "SUBMITTED" | "FORWARDED" | "OFFER_RECEIVED" | "APPLICATION_READY";

export interface FundingCampaignSubmission {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  city: string;
  parking_spaces: number;
  status: FundingCampaignStatus;
  forwarded_to_electrician: string | null;
  created_at: string;
  updated_at: string;
}

interface ListParams {
  status?: FundingCampaignStatus;
  q?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
  sort?: "created_at" | "updated_at";
  order?: "asc" | "desc";
}

interface ListResponse {
  ok: true;
  data: FundingCampaignSubmission[];
  total: number;
  limit: number;
  offset: number;
}

export interface FundingCampaignHistoryEntry {
  id: string;
  from_status: FundingCampaignStatus | null;
  to_status: FundingCampaignStatus;
  electrician: string | null;
  note: string | null;
  changed_by: string;
  changed_at: string;
}

export interface FundingCampaignNote {
  id: string;
  body: string;
  created_by: string;
  created_at: string;
}

export interface FundingCampaignSubmissionDetail extends FundingCampaignSubmission {
  street: string | null;
  postal_code: string | null;
  electrician_consent: number | null;
  electrician_consent_at: string | null;
  answers: Record<string, string> | null;
  cc_emails: string[] | null;
  source_url: string | null;
}

interface DetailResponse {
  ok: true;
  data: FundingCampaignSubmissionDetail;
  history: FundingCampaignHistoryEntry[];
  notes: FundingCampaignNote[];
}

export interface UpdateStatusPayload {
  status: FundingCampaignStatus;
  electrician?: string;
  note?: string;
}

export interface UpdateStatusResponse {
  ok: true;
  data: {
    id: string;
    status: FundingCampaignStatus;
    updated_at: string;
    forwarded_to_electrician: string | null;
    history_id: string;
    email_sent: boolean;
    email_recipients: string[];
  };
}

export async function getFundingCampaignSubmission(id: string): Promise<DetailResponse> {
  try {
    const response = await marketingWebsiteApi.get(`/api/admin/wizard/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function updateFundingCampaignStatus(
  id: string,
  payload: UpdateStatusPayload
): Promise<UpdateStatusResponse> {
  try {
    const response = await marketingWebsiteApi.patch(`/api/admin/wizard/${id}/status`, payload);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export interface AddNotePayload {
  body: string;
}

export interface AddNoteResponse {
  ok: true;
  data: FundingCampaignNote;
}

export async function addFundingCampaignNote(
  id: string,
  payload: AddNotePayload
): Promise<AddNoteResponse> {
  try {
    const response = await marketingWebsiteApi.post(`/api/admin/wizard/${id}/notes`, payload);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getFundingCampaignSubmissions(params: ListParams = {}): Promise<ListResponse> {
  try {
    const response = await marketingWebsiteApi.get("/api/admin/wizard", { params });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
